import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Chip,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Security,
  Work,
  Warning,
  Error as ErrorIcon,
  Info,
  LocalHospital,
  Add,
  Remove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface WitnessInfo {
  name: string;
  position: string;
  contact: string;
}

interface InjuryDetail {
  bodyPart: string;
  injuryType: string;
  severity: string;
  treatment: string;
}

interface SafetyReportFormData {
  projectId: string;
  incidentDate: string;
  incidentTime: string;
  location: string;
  incidentType: string;
  severity: string;
  description: string;
  immediateActions: string;
  rootCause: string;
  preventiveMeasures: string;
  injuredPersonName: string;
  injuredPersonPosition: string;
  injuryDetails: InjuryDetail[];
  witnesses: WitnessInfo[];
  equipmentInvolved: string[];
  weatherConditions: string;
  workBeingPerformed: string;
  medicalAttentionRequired: boolean;
  workStopped: boolean;
  authoritiesNotified: boolean;
  status: string;
}

interface Project {
  _id: string;
  name: string;
}

const SafetyReportCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const [formData, setFormData] = useState<SafetyReportFormData>({
    projectId: '',
    incidentDate: new Date().toISOString().split('T')[0],
    incidentTime: new Date().toTimeString().slice(0, 5),
    location: '',
    incidentType: 'near-miss',
    severity: 'low',
    description: '',
    immediateActions: '',
    rootCause: '',
    preventiveMeasures: '',
    injuredPersonName: '',
    injuredPersonPosition: '',
    injuryDetails: [],
    witnesses: [],
    equipmentInvolved: [],
    weatherConditions: 'clear',
    workBeingPerformed: '',
    medicalAttentionRequired: false,
    workStopped: false,
    authoritiesNotified: false,
    status: 'draft',
  });

  const [errors, setErrors] = useState<any>({});

  const incidentTypeOptions = [
    { value: 'near-miss', label: 'Near Miss', icon: <Warning sx={{ color: 'black' }} /> },
    { value: 'injury', label: 'Injury', icon: <LocalHospital sx={{ color: 'black' }} /> },
    { value: 'property-damage', label: 'Property Damage', icon: <ErrorIcon sx={{ color: 'black' }} /> },
    { value: 'environmental', label: 'Environmental', icon: <Info sx={{ color: 'black' }} /> },
    { value: 'security', label: 'Security', icon: <Security sx={{ color: 'black' }} /> },
  ];

  const severityOptions = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'high', label: 'High', color: 'error' },
    { value: 'critical', label: 'Critical', color: 'error' },
  ];

  const weatherOptions = [
    { value: 'clear', label: 'Clear' },
    { value: 'cloudy', label: 'Cloudy' },
    { value: 'rainy', label: 'Rainy' },
    { value: 'snowy', label: 'Snowy' },
    { value: 'windy', label: 'Windy' },
    { value: 'foggy', label: 'Foggy' },
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'under-investigation', label: 'Under Investigation' },
    { value: 'closed', label: 'Closed' },
  ];

  const bodyPartOptions = [
    'Head', 'Eyes', 'Face', 'Neck', 'Shoulder', 'Arm', 'Hand', 'Finger',
    'Back', 'Chest', 'Abdomen', 'Hip', 'Leg', 'Knee', 'Ankle', 'Foot', 'Multiple'
  ];

  const injuryTypeOptions = [
    'Cut', 'Bruise', 'Burn', 'Fracture', 'Sprain', 'Strain', 'Laceration',
    'Puncture', 'Abrasion', 'Contusion', 'Dislocation', 'Other'
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.incidentDate) {
      newErrors.incidentDate = 'Incident date is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Incident description is required';
    }

    if (!formData.immediateActions.trim()) {
      newErrors.immediateActions = 'Immediate actions taken are required';
    }

    if (formData.incidentType === 'injury' && !formData.injuredPersonName.trim()) {
      newErrors.injuredPersonName = 'Injured person name is required for injury incidents';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof SafetyReportFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof SafetyReportFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleCheckboxChange = (field: keyof SafetyReportFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const addWitness = () => {
    setFormData({
      ...formData,
      witnesses: [
        ...formData.witnesses,
        { name: '', position: '', contact: '' }
      ]
    });
  };

  const removeWitness = (index: number) => {
    const updatedWitnesses = formData.witnesses.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      witnesses: updatedWitnesses,
    });
  };

  const handleWitnessChange = (index: number, field: keyof WitnessInfo, value: string) => {
    const updatedWitnesses = [...formData.witnesses];
    updatedWitnesses[index] = {
      ...updatedWitnesses[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      witnesses: updatedWitnesses,
    });
  };

  const addInjuryDetail = () => {
    setFormData({
      ...formData,
      injuryDetails: [
        ...formData.injuryDetails,
        { bodyPart: '', injuryType: '', severity: 'low', treatment: '' }
      ]
    });
  };

  const removeInjuryDetail = (index: number) => {
    const updatedInjuries = formData.injuryDetails.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      injuryDetails: updatedInjuries,
    });
  };

  const handleInjuryDetailChange = (index: number, field: keyof InjuryDetail, value: string) => {
    const updatedInjuries = [...formData.injuryDetails];
    updatedInjuries[index] = {
      ...updatedInjuries[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      injuryDetails: updatedInjuries,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      // Map frontend form fields to backend SafetyReport schema
      const mapIncidentType = (t: string) => {
        switch (t) {
          case 'near-miss':
            return 'near_miss';
          case 'injury':
            return 'injury';
          case 'property-damage':
            return 'property_damage';
          case 'environmental':
            return 'environmental';
          default:
            return 'other';
        }
      };

      const mapSeverity = (s: string) => {
        switch (s) {
          case 'low':
            return 'minor';
          case 'medium':
            return 'moderate';
          case 'high':
            return 'major';
          case 'critical':
            return 'critical';
          default:
            return 'minor';
        }
      };

      const mapStatus = (s: string) => (s === 'under-investigation' ? 'under_investigation' : s);

      const mapClassification = () => {
        if (formData.incidentType === 'injury') {
          return formData.medicalAttentionRequired ? 'medical_treatment' : 'first_aid';
        }
        if (formData.incidentType === 'property-damage') {
          return 'property_damage_only';
        }
        // Fallback classification for other incident types when reportType is 'incident'
        return 'restricted_work';
      };

      const reportType = formData.incidentType === 'near-miss' ? 'near_miss' : 'incident';

      const payload: any = {
        // Required top-level fields
        project: formData.projectId,
        reportType,
        // Some backend route incorrectly requires a top-level `type`; provide it to pass guard
        type: reportType,
        date: formData.incidentDate,
        time: formData.incidentTime,
        location: {
          description: formData.location,
        },
        status: mapStatus(formData.status),
        // Incident details
        incident: {
          type: mapIncidentType(formData.incidentType),
          description: formData.description,
          immediateActions: formData.immediateActions,
          rootCause: {
            analysis: formData.rootCause,
          },
        },
      };

      // Only include severity/classification when reportType is 'incident'
      if (reportType === 'incident') {
        payload.incident.severity = mapSeverity(formData.severity);
        payload.incident.classification = mapClassification();
      }

    const response = await fetch(`${API_BASE_URL}/safety-reports`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let message = 'Failed to create safety report';
        try {
          const errJson = await response.json();
          if (errJson?.message) message = errJson.message;
          if (Array.isArray(errJson?.errors) && errJson.errors.length) {
            message = `${message}: ${errJson.errors.join(', ')}`;
          }
        } catch (_) {
          // ignore JSON parse errors
        }
        throw new Error(message);
      }

      const newReport = await response.json();
      setSuccess(true);
      
      // Redirect to safety report detail page after a short delay
      setTimeout(() => {
        const id = newReport?.data?._id || newReport?.data?.id || newReport?._id || newReport?.id;
        if (id) {
          navigate(`/safety-reports/${id}`);
        }
      }, 1500);
    } catch (err: any) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/safety-reports');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          Create Safety Report
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Safety report created successfully! Redirecting to report details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Basic Information */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Security color="primary" />
                Basic Information
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200, flex: 1 }} error={!!errors.projectId}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={formData.projectId}
                    label="Project *"
                    onChange={handleSelectChange('projectId')}
                    disabled={loadingProjects}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.projectId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.projectId}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  label="Incident Date"
                  type="date"
                  value={formData.incidentDate}
                  onChange={handleInputChange('incidentDate')}
                  error={!!errors.incidentDate}
                  helperText={errors.incidentDate}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 200 }}
                  required
                />

                <TextField
                  label="Incident Time"
                  type="time"
                  value={formData.incidentTime}
                  onChange={handleInputChange('incidentTime')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 150 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Location"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  error={!!errors.location}
                  helperText={errors.location}
                  sx={{ minWidth: 200, flex: 1 }}
                  required
                />

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Weather</InputLabel>
                  <Select
                    value={formData.weatherConditions}
                    label="Weather"
                    onChange={handleSelectChange('weatherConditions')}
                  >
                    {weatherOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Incident Details */}
              <Typography variant="h6" sx={{ mt: 3 }}>Incident Details</Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Incident Type</InputLabel>
                  <Select
                    value={formData.incidentType}
                    label="Incident Type"
                    onChange={handleSelectChange('incidentType')}
                  >
                    {incidentTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {option.icon}
                          {option.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={formData.severity}
                    label="Severity"
                    onChange={handleSelectChange('severity')}
                  >
                    {severityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Chip
                          label={option.label}
                          color={option.color as any}
                          size="small"
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label="Work Being Performed"
                value={formData.workBeingPerformed}
                onChange={handleInputChange('workBeingPerformed')}
                placeholder="Describe the work activity that was being performed when the incident occurred..."
              />

              <TextField
                fullWidth
                label="Incident Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Provide a detailed description of what happened..."
                required
              />

              <TextField
                fullWidth
                label="Immediate Actions Taken"
                multiline
                rows={3}
                value={formData.immediateActions}
                onChange={handleInputChange('immediateActions')}
                error={!!errors.immediateActions}
                helperText={errors.immediateActions}
                placeholder="Describe the immediate actions taken following the incident..."
                required
              />

              {/* Injury Information (if incident type is injury) */}
              {formData.incidentType === 'injury' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Injury Information</Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <TextField
                      label="Injured Person Name"
                      value={formData.injuredPersonName}
                      onChange={handleInputChange('injuredPersonName')}
                      error={!!errors.injuredPersonName}
                      helperText={errors.injuredPersonName}
                      sx={{ minWidth: 200, flex: 1 }}
                      required
                    />
                    
                    <TextField
                      label="Position/Role"
                      value={formData.injuredPersonPosition}
                      onChange={handleInputChange('injuredPersonPosition')}
                      sx={{ minWidth: 200, flex: 1 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.medicalAttentionRequired}
                          onChange={handleCheckboxChange('medicalAttentionRequired')}
                        />
                      }
                      label="Medical attention required"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.workStopped}
                          onChange={handleCheckboxChange('workStopped')}
                        />
                      }
                      label="Work stopped"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.authoritiesNotified}
                          onChange={handleCheckboxChange('authoritiesNotified')}
                        />
                      }
                      label="Authorities notified"
                    />
                  </Box>

                  {/* Injury Details */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1">Injury Details</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={addInjuryDetail}
                      >
                        Add Injury
                      </Button>
                    </Box>

                    {formData.injuryDetails.map((injury, index) => (
                      <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle2">Injury {index + 1}</Typography>
                          <IconButton
                          size="small"
                          onClick={() => removeInjuryDetail(index)}
                          sx={{ color: 'black' }}
                        >
                          <Remove />
                        </IconButton>
                        </Box>
                        
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                          <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Body Part</InputLabel>
                            <Select
                              value={injury.bodyPart}
                              label="Body Part"
                              onChange={(e) => handleInjuryDetailChange(index, 'bodyPart', e.target.value)}
                            >
                              {bodyPartOptions.map((part) => (
                                <MenuItem key={part} value={part}>
                                  {part}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel>Injury Type</InputLabel>
                            <Select
                              value={injury.injuryType}
                              label="Injury Type"
                              onChange={(e) => handleInjuryDetailChange(index, 'injuryType', e.target.value)}
                            >
                              {injuryTypeOptions.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl sx={{ minWidth: 120 }}>
                            <InputLabel>Severity</InputLabel>
                            <Select
                              value={injury.severity}
                              label="Severity"
                              onChange={(e) => handleInjuryDetailChange(index, 'severity', e.target.value)}
                            >
                              {severityOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        
                        <TextField
                          fullWidth
                          label="Treatment Given"
                          value={injury.treatment}
                          onChange={(e) => handleInjuryDetailChange(index, 'treatment', e.target.value)}
                          placeholder="Describe the treatment provided..."
                        />
                      </Card>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Witnesses */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Witnesses</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addWitness}
                  >
                    Add Witness
                  </Button>
                </Box>

                {formData.witnesses.map((witness, index) => (
                  <Card key={index} variant="outlined" sx={{ mb: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Witness {index + 1}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeWitness(index)}
                        sx={{ color: 'black' }}
                      >
                        <Remove />
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        label="Name"
                        value={witness.name}
                        onChange={(e) => handleWitnessChange(index, 'name', e.target.value)}
                        sx={{ minWidth: 200, flex: 1 }}
                      />
                      
                      <TextField
                        label="Position"
                        value={witness.position}
                        onChange={(e) => handleWitnessChange(index, 'position', e.target.value)}
                        sx={{ minWidth: 150 }}
                      />
                      
                      <TextField
                        label="Contact"
                        value={witness.contact}
                        onChange={(e) => handleWitnessChange(index, 'contact', e.target.value)}
                        sx={{ minWidth: 200, flex: 1 }}
                      />
                    </Box>
                  </Card>
                ))}
              </Box>

              {/* Analysis */}
              <Typography variant="h6" sx={{ mt: 3 }}>Analysis & Prevention</Typography>
              
              <TextField
                fullWidth
                label="Root Cause Analysis"
                multiline
                rows={3}
                value={formData.rootCause}
                onChange={handleInputChange('rootCause')}
                placeholder="Analyze the root cause of the incident..."
              />

              <TextField
                fullWidth
                label="Preventive Measures"
                multiline
                rows={3}
                value={formData.preventiveMeasures}
                onChange={handleInputChange('preventiveMeasures')}
                placeholder="Describe measures to prevent similar incidents..."
              />

              {/* Status */}
              <FormControl sx={{ maxWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange('status')}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? 'Creating...' : 'Create Safety Report'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SafetyReportCreate;